import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useBasket } from '../state/basket-provider';
import { useCheckoutDraft } from '../state/checkout';
import { useSession } from '../state/session';

/**
 * MODE B2: the shop serving this visit has stopped trading, so the visit starts
 * again (founder decision, 2026-09-20).
 *
 * `error.shop_changed` is the 409 that says the bound shop was deactivated,
 * suspended from allocation or lost its payout verification since the scan. It
 * is NOT `error.choose_shop`: there is no chooser in B2 and the guest picked
 * nothing, so the only move is the one this component makes.
 *
 * Three things, in the order they matter:
 *
 *  1. the BASKET is emptied. Its lines came off a different house's shelf and
 *     the next shop does not sell them - carrying them over would price at
 *     zero and be refused at the Total, which is the failure `Basket.tsx`
 *     already exists to keep off the payment sheet;
 *  2. the SESSION is re-resolved, which is what re-computes the closest shop.
 *     Only a scan may move a B2 binding, and this re-scans the token the phone
 *     already holds - no code has to be read again;
 *  3. the guest lands back on the shelf - the new shop's - told ONCE what
 *     happened. The notice rides in the query string rather than in a store,
 *     so nothing has to be cleared afterwards and a second failure cannot show
 *     it twice.
 *
 * The landing WAITS for the re-scan. The shelf is the screen that most often
 * meets this refusal and is also where it lands, so a plain redirect would
 * leave the same errored answer on the same route: every answer but the
 * session's is reset once the new session is in, and the shelf asks again
 * under the new binding.
 *
 * One component rather than the same effect on every screen that can meet the
 * refusal, for the reason `VoidShopRedirect` is one: the vitrine, the picker
 * and the first screen all answer it, and three copies are one dispatch away
 * from disagreeing.
 */
export function ShopChangedRedirect(): null {
  const { dispatch } = useBasket();
  const { update } = useCheckoutDraft();
  const { retry } = useSession();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  useEffect(() => {
    dispatch({ type: 'CLEAR' });
    /* The delivery time goes with the basket: it was that shop's offer. */
    update({ serviceDay: null, windowStart: null, selectedDays: [] });
    void retry().then(() => {
      navigate(`/menu?${SHOP_CHANGED_PARAM}=1`, { replace: true });
      return queryClient.resetQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
    });
  }, [dispatch, update, retry, queryClient, navigate]);
  return null;
}

/** The one-shot notice on the shelf's URL, read by `screens/Menu`. */
export const SHOP_CHANGED_PARAM = 'changed';
